import { Box, Input, Spinner } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

export type FormattedData = {
  displayString: string;
  subString?: string;
  value: string;
};

type Props = {
  data: FormattedData[];
  handleSearch: () => void;
  isLoading: boolean;
  onSelect: (value: string) => void;
  placeholder: string;
  // eslint-disable-next-line react/require-default-props
  isDisabled?: boolean;
  searchString: string;
  setSearchString: (value: string) => void;
  [key: string]: unknown;
};

export default function TypeAhead({
  data,
  handleSearch,
  isLoading,
  onSelect,
  placeholder,
  searchString,
  isDisabled = false,
  setSearchString,
  clientFacing = false,
  ...rest
}: Props) {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const resultsRef = useRef([]);
  const intervalRef = useRef(null);

  useEffect(() => {
    // Reset selected index when data changes
    setSelectedIndex(-1);
  }, [searchString]);

  const debounceSearch = (value) => {
    setSearchString(value);
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
      intervalRef.current = null;
    }
    intervalRef.current = setTimeout(() => {
      handleSearch();
    }, 1200);
  };

  const handleKeyDown = (event) => {
    switch (event.key) {
      case 'ArrowUp':
        setSelectedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : data.length - 1));
        break;
      case 'ArrowDown':
        setSelectedIndex((prevIndex) => (prevIndex < data.length - 1 ? prevIndex + 1 : 0));
        break;
      case 'Enter':
        if (selectedIndex >= 0 && selectedIndex < data.length) {
          onSelect(data[selectedIndex].value);
        }
        break;
      default:
        break;
    }
  };

  const results = () => {
    if (data.length) {
      const options = data.map((el, index) => (
        <Box
          _hover={{ background: 'primary.50' }}
          alignItems="start"
          border="1px solid #fbfbfb"
          display="flex"
          flexDirection="column"
          key={el.value}
          onClick={() => onSelect(el.value)}
          padding="0.5rem"
          ref={(ref) => {
            // Set the ref for the current index
            resultsRef.current[index] = ref;
          }}
          bg={selectedIndex === index ? 'gray.200' : 'white'}
        >
          <Box fontWeight="600" noOfLines={2} textOverflow="ellipsis">
            {el.displayString}
          </Box>
          {!clientFacing ? <Box color="grey">{el.subString}</Box> : null}
        </Box>
      ));
      return <>{options}</>;
    }
    return <Box padding="1rem">No results found</Box>;
  };

  return (
    <Box>
      <Input
        onChange={(e) => debounceSearch(e.target.value)}
        onKeyDown={handleKeyDown}
        focusBorderColor="primary.400"
        isDisabled={isDisabled}
        placeholder={placeholder || 'Start typing to search'}
        value={searchString}
        {...rest}
      />
      <Box
        background="white"
        border="1px solid #e6e6e6"
        borderRadius={10}
        display={searchString.length > 0 ? 'flex' : 'none'}
        flexDirection="column"
        width="100%"
        overflow="hidden"
      >
        {!isLoading ? results() : <Spinner color="primary.400" margin="1rem auto" />}
      </Box>
    </Box>
  );
}
